import { all, call, put, takeEvery, takeLatest, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { logError } from 'src/loggingManager';
import { getSurfaceProperties } from 'src/selectors/pages/editor';
import { retrieveRulesForProduct, fetchProduct, loadVersions, translateSku } from 'src/components/skus/saga';
import { getSkuById } from 'src/selectors/skus';
import {
    applyVariables,
    changeEditorSkuSuccess,
    changeEditorSkuFailed,
    requestSurfaces,
    retrieveSurfacesFailed,
    retrieveSurfacesSuccess,
    changeEditorSku,
    validateEditorSku,
    validateEditorSkuSuccess,
    validateEditorSkuFailed,
} from './slice';
import { fetchSurfaces } from 'src/components/scenes/saga';
import { resolveEffectiveSku } from 'src/util/skuKey';

/** Identifies which sku/version/merchantVariables selection a surfaces fetch is *for* — computed
 * identically by both `changeSkuForEditor` and `getSurfaces` (the two independent producers of
 * `retrieveSurfacesSuccess`) so a response from either can be told apart from a newer, superseded
 * one. Mirrors `decorationSpecs/saga.ts`'s own `buildCacheKey`. */
function buildSurfacesFetchKey({
    sku,
    skuVersion,
    merchantSku,
    merchantVersion,
    merchantVariables,
}: {
    sku?: string;
    skuVersion?: number;
    merchantSku?: string;
    merchantVersion?: number;
    merchantVariables?: Record<string, string>;
}): string {
    return `${sku}-${skuVersion}-${merchantSku}-${merchantVersion}-${JSON.stringify(merchantVariables ?? {})}`;
}

export function* changeSkuForEditor(): Generator<any, void, any> {
    try {
        const { sku, skuVersion, merchantSku, merchantVersion, merchantVariables } =
            yield* select(getSurfaceProperties);
        const surfacesFetchKey = buildSurfacesFetchKey({
            sku,
            skuVersion,
            merchantSku,
            merchantVersion,
            merchantVariables,
        });
        const { effectiveSku, effectiveVersion } = resolveEffectiveSku({
            sku,
            skuVersion,
            merchantSku,
            merchantVersion,
        });
        const payload = { sku: effectiveSku, skuVersion: effectiveVersion };

        if (payload.sku) {
            yield* call(fetchProduct, {
                payload: { sku: payload.sku, skuVersion: payload.skuVersion, throwAgain: true },
                type: 'skus/fetchProduct',
            } as any);
            yield* put(changeEditorSkuSuccess());
            yield* put(requestSurfaces({ surfacesFetchKey }));
            try {
                const surfaces = yield* call(fetchSurfaces, {
                    sku,
                    skuVersion,
                    merchantSku,
                    merchantVersion,
                    merchantVariables,
                });
                yield* put(retrieveSurfacesSuccess({ surfacesFetchKey, surfaces }));
            } catch (error: any) {
                yield* put(retrieveSurfacesFailed());
            }
            yield* call(retrieveRulesForProduct, { payload, type: 'skus/retrieveRulesForProduct' } as any);
        } else {
            logError('Failed to change product sku in editor page: Sku is required');
            yield* put(changeEditorSkuFailed(payload.sku));
        }
    } catch (error: any) {
        logError(`Failed to change product sku in editor page: ${error}`);
        yield* put(changeEditorSkuFailed(error));
    }
}

function* validateSkuForEditor({ payload }: PayloadAction<string>): Generator<any, void, any> {
    try {
        if (payload) {
            let version: number | undefined;
            let hasNoVersions = payload.startsWith('VIP-');
            // In case the sku does not support versions, don't try to get the versions
            if (!hasNoVersions) {
                yield* call(loadVersions, { payload, type: 'skus/loadVersions' } as any);
                const product: any = yield* select(getSkuById, payload);
                version = !product.versions || product.versions.length === 0 ? undefined : product.versions[0].version;
                hasNoVersions = product.isV1Product;
            }

            const mcp: any = yield* call(translateSku, { sku: payload, skuVersion: version });

            // It's a PRD Product
            if (mcp.platformId !== payload) {
                yield* put(
                    validateEditorSkuSuccess({
                        sku: mcp.platformId,
                        skuVersion: mcp.platformIdVersion,
                        variables: {},
                        merchantSku: payload,
                        merchantVersion: version,
                        merchantVariables: {},
                    }),
                );
            } else {
                yield* put(
                    validateEditorSkuSuccess({
                        sku: payload,
                        skuVersion: version,
                        variables: {},
                        merchantSku: payload,
                        merchantVersion: version,
                        merchantVariables: {},
                    }),
                );
            }

            // If the sku doesn't support versions, try to get the surfaces right away.
            if (hasNoVersions) {
                yield* call(changeSkuForEditor);
            }
        } else {
            logError('Failed to change product sku in editor page: Sku is required');
            yield* put(validateEditorSkuFailed(payload));
        }
    } catch (error: any) {
        logError(`Failed to change product sku in editor page: ${payload} ${error}`);
        yield* put(validateEditorSkuFailed(payload));
    }
}

export function* getSurfaces(): Generator<any, void, any> {
    const { sku, skuVersion, isResolved, merchantSku, merchantVersion, merchantVariables } =
        yield* select(getSurfaceProperties);
    if (isResolved) {
        const surfacesFetchKey = buildSurfacesFetchKey({
            sku,
            skuVersion,
            merchantSku,
            merchantVersion,
            merchantVariables,
        });
        try {
            yield* put(requestSurfaces({ surfacesFetchKey }));
            // need to translate any PRD to MCP including selected attributes
            const mcp: any = yield* call(translateSku as any, {
                sku: merchantSku,
                skuVersion: merchantVersion,
                attributes: merchantVariables,
            });

            // It's a PRD Product
            if (mcp.platformId !== merchantSku) {
                yield* put(
                    validateEditorSkuSuccess({
                        sku: mcp.platformId,
                        skuVersion: mcp.platformIdVersion,
                        variables: mcp.platformIdAttributes,
                        merchantSku,
                        merchantVersion,
                        merchantVariables,
                    }),
                );
            } else {
                yield* put(
                    validateEditorSkuSuccess({
                        sku: merchantSku,
                        skuVersion: merchantVersion,
                        variables: merchantVariables,
                        merchantSku,
                        merchantVersion,
                        merchantVariables,
                    }),
                );
            }

            const surfaces: any = yield* call(fetchSurfaces as any, {
                sku: mcp.platformId,
                skuVersion: mcp.platformIdVersion,
                variables: mcp.platformIdAttributes,
                merchantSku,
                merchantVersion,
                merchantVariables,
            });
            yield* put(retrieveSurfacesSuccess({ surfacesFetchKey, surfaces }));
        } catch (error: any) {
            logError(`Failed to get surfaces despite a resolved variable set. ${error}`);
            yield* put(retrieveSurfacesFailed());
        }
    }
}

export default function* editorSurfacesSagas() {
    return yield* all([
        yield* takeEvery(changeEditorSku, changeSkuForEditor),
        yield* takeEvery(validateEditorSku as any, validateSkuForEditor),
        // takeLatest, not takeEvery: getSurfaces does an async translateSku round trip before
        // dispatching validateEditorSkuSuccess with the merchantVariables snapshot it read at
        // the start of its own run. With takeEvery, two overlapping instances could settle out
        // of order, and the older (now-stale) one's dispatch would silently revert
        // state.merchantVariables to an earlier selection — the picker's own displayed value —
        // after a newer pick had already landed. takeLatest cancels an in-flight instance as
        // soon as a newer applyVariables fires, so only the latest selection's dispatch survives.
        yield* takeLatest(applyVariables, getSurfaces),
    ]);
}
